import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Spin, Table, Input, Button, DatePicker, Drawer, Space, Select } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  RiseOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import StatCard from '../../../../components/StatCard';
import ReportService from '../../../../services/ReportService';

dayjs.extend(isBetween);

const SalesSummaries = () => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [salesData, setSalesData] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [branchCode, setBranchCode] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState([]);
  const [draftBranchCode, setDraftBranchCode] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const companyCode = userDetails?.CompanyCode || '';

  const fetchBranches = async () => {
    try {
      setBranchesLoading(true);
      const response = await ReportService.company.getBranches({
        token,
        CompanyCode: companyCode,
      });
      if (response.status === 200) {
        const branchList = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setBranches(branchList);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setBranchesLoading(false);
    }
  };

  const fetchSalesDataWithParams = async (customDateRange = null, customBranchCode = null) => {
    try {
      setSalesLoading(true);
      const currentDateRange = customDateRange || dateRange;
      const currentBranchCode = customBranchCode !== null ? customBranchCode : branchCode;

      let StartDate;
      let EndDate;
      if (currentDateRange && currentDateRange.length === 2) {
        StartDate = dayjs(currentDateRange[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
        EndDate = dayjs(currentDateRange[1]).add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss');
      }
      const params = {
        token,
        CompanyCode: companyCode,
        StartDate,
        EndDate,
      };

      if (currentBranchCode && currentBranchCode.trim() !== '') {
        params.BranchCode = currentBranchCode;
      }

      const response = await ReportService.sales.getSalesSummaries(params);
      if (response.status === 200) {
        const rows = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const mapped = rows.map((r, idx) => ({
          key: String(idx + 1),
          product: r.UrunAdi,
          portion: r.PorsiyonAdi,
          quantity: Number(r.Miktar) || 0,
          totalPrice: Number(r.ToplamSatis) || 0,
        }));
        setSalesData(mapped);
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setSalesLoading(false);
    }
  };

  const fetchSalesData = async () => {
    return fetchSalesDataWithParams();
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSalesData(),
      fetchBranches()
    ]).finally(() => {
      setLoading(false);
    });
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
  };

  const salesColumns = [
    { title: 'Ürün', dataIndex: 'product', key: 'product', width: isMobile ? '40%' : '40%', render: (text) => <span className="text-sm font-medium">{text}</span> },
    { title: 'Porsiyon', dataIndex: 'portion', key: 'portion', width: isMobile ? '25%' : '25%', render: (text) => <span className="text-sm">{text}</span> },
    { title: 'Adet', dataIndex: 'quantity', key: 'quantity', width: isMobile ? '15%' : '15%', render: (value) => <span className="text-sm font-medium">{value}</span> },
    { title: 'Toplam', dataIndex: 'totalPrice', key: 'totalPrice', width: isMobile ? '20%' : '20%', render: (value) => <span className="font-semibold text-green-600">{formatCurrency(value)}</span> },
  ];

  const filteredSalesData = salesData.filter((row) => {
    if (searchText) {
      const needle = searchText.toLowerCase();
      const matches = (
        String(row.product || '').toLowerCase().includes(needle) ||
        String(row.portion || '').toLowerCase().includes(needle)
      );
      if (!matches) return false;
    }
    return true;
  });

  const derivedStats = useMemo(() => {
    const totalRows = filteredSalesData.length;
    const totalSales = filteredSalesData.reduce((sum, r) => sum + Number(r.totalPrice || 0), 0);
    const totalQuantity = filteredSalesData.reduce((sum, r) => sum + Number(r.quantity || 0), 0);
    const averageProductPrice = totalQuantity > 0 ? totalSales / totalQuantity : 0;

    const productQty = new Map();
    filteredSalesData.forEach((r) => {
      const q = Number(r.quantity || 0);
      if (r.product) productQty.set(r.product, (productQty.get(r.product) || 0) + q);
    });
    const topProduct = Array.from(productQty.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

    return { totalRows, totalSales, totalQuantity, averageProductPrice, topProduct };
  }, [filteredSalesData]);

  const openFilters = () => {
    setDraftDateRange(dateRange);
    setDraftBranchCode(branchCode);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    const newDateRange = draftDateRange || [];
    const newBranchCode = draftBranchCode || '';

    setDateRange(newDateRange);
    setBranchCode(newBranchCode);
    setIsFilterOpen(false);

    fetchSalesDataWithParams(newDateRange, newBranchCode);
  };

  const clearFilters = () => {
    setDraftDateRange([]);
    setDraftBranchCode('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between px-1 sm:px-0 py-1 sm:py-2">
        <div className="flex items-center gap-2">
          <ShoppingCartOutlined className="text-blue-600" />
          <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800">Satış Özetleri</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
        </div>
      </div>

      <Row gutter={[12, 12]} className="mt-1">
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam Satış"
            value={formatCurrency(derivedStats.totalSales)}
            suffix=""
            icon={<DollarOutlined />}
            gradientFrom="from-emerald-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam Adet"
            value={derivedStats.totalQuantity}
            suffix="adet"
            icon={<ShoppingCartOutlined />}
            gradientFrom="from-blue-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Ortalama Ürün Fiyatı"
            value={formatCurrency(derivedStats.averageProductPrice)}
            suffix=""
            icon={<RiseOutlined />}
            gradientFrom="from-purple-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="En Çok Satan"
            value={derivedStats.topProduct}
            suffix=""
            icon={<UserOutlined />}
            gradientFrom="from-orange-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
      </Row>

      <div className="mt-4 sm:mt-5">
        <Card title={<div className="mt-2"><h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>Satış Detayları</h2></div>} bordered={false} className="shadow-lg rounded-2xl border border-gray-100 bg-white" bodyStyle={{ padding: isMobile ? 12 : 18 }}>
          <div className="mb-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2">
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Input allowClear value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Müşteri, ürün veya sipariş no ara..." prefix={<SearchOutlined className="text-gray-400" />} className="w-full sm:w-80" />
              <Button onClick={() => fetchSalesData()} loading={salesLoading} icon={<ReloadOutlined />}>Yenile</Button>
            </div>
          </div>

          <Table columns={salesColumns} dataSource={filteredSalesData} loading={salesLoading} rowKey="key" sticky pagination={{ defaultPageSize: isMobile ? 5 : 10, size: isMobile ? 'small' : 'default', showTotal: (total) => (<span className={`${isMobile ? 'text-sm' : 'text-base'}`}>Toplam {total} kayıt</span>), showSizeChanger: !isMobile }} className="sales-table [&_.ant-table-thead_tr_th]:bg-gray-50 [&_.ant-table-row:hover]:bg-blue-50/30" size={isMobile ? 'small' : 'middle'} rowClassName={() => (isMobile ? 'text-[13px]' : 'text-[15px]')} scroll={isMobile ? { x: true } : undefined} summary={(pageData) => { let total = 0; let totalQuantity = 0; pageData.forEach((row) => { total += Number(row.totalPrice || 0); totalQuantity += Number(row.quantity || 0); }); return (<Table.Summary fixed><Table.Summary.Row><Table.Summary.Cell index={0} colSpan={2}><span className="font-medium">Toplam</span></Table.Summary.Cell><Table.Summary.Cell index={2}><span className="font-medium">{totalQuantity}</span></Table.Summary.Cell><Table.Summary.Cell index={3}><span className="font-semibold text-green-600">{formatCurrency(total)}</span></Table.Summary.Cell></Table.Summary.Row></Table.Summary>); }} />
        </Card>
      </div>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Tarih Aralığı</span>
            <DatePicker.RangePicker value={draftDateRange} onChange={(vals) => setDraftDateRange(vals || [])} format={'YYYY-MM-DD'} className="w-full" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftBranchCode}
              onChange={(value) => setDraftBranchCode(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              loading={branchesLoading}
              allowClear
            >
              <Select.Option value="">Tüm Şubeler</Select.Option>
              {branches.map((branch) => (
                <Select.Option key={branch.ID} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default SalesSummaries;